/**
 * PRODUCTS-WORKSPACE-000001 — Products founder reference workspace.
 * Framework: COMMERCE-WORKSPACE-FRAMEWORK-000001.
 * SoT: marketsync_canonical_objects domain=catalog.
 * No API / MarketSync / backend changes. No invented catalog metrics.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  amazonConnectApi,
  type CatalogProduct,
  type CatalogProductPage,
} from '../../api/amazonconnect';
import {
  PAGE_SIZE_OPTIONS,
  PlatformDataGrid,
  type DataGridColumn,
  type PageSizeOption,
} from '../../components/platform/PlatformDataGrid';
import {
  BulkActionBar,
  BulkActionButton,
  ConnectEmptyAction,
  ConnectionToolbarSelect,
  NoConnectionBanner,
  QuickFilterBar,
  SyncOnConnectAction,
  ToolbarFilterSelect,
  buildCommerceGridMeta,
  copyLinesToClipboard,
  useCommerceConnections,
  useCommerceQueryBootstrap,
} from '../../commerce-workspace';
import { getMembershipId, getWorkspaceId } from '../../lib/auth-storage';
import { FounderCommerceShell } from './workspace/FounderCommerceShell';
import {
  AsinLink,
  ComingSoonCell,
  SkuLink,
  WorkspaceLegend,
} from './workspace/commerceFields';
import { ProductSummaryStrip } from './workspace/ProductSummaryStrip';
import { productStatusText, summarizeProducts } from './workspace/productStatus';
import {
  PRODUCT_STATUS_OPTS,
  productsWorkspaceConfig,
} from './workspace/productsWorkspaceConfig';
import { useCommerceDomainHealth } from './workspace/useCommerceDomainHealth';

function imageUrl(p: CatalogProduct): string | undefined {
  if (p.image_url) return p.image_url;
  const pay = p.payload;
  if (!pay) return undefined;
  const candidates = [
    pay.main_image,
    pay.image,
    pay.image_url,
    (pay as { mainImage?: { link?: string } }).mainImage?.link,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.startsWith('http')) return c;
  }
  return undefined;
}

export function MarketSyncProductsPage() {
  const cfg = productsWorkspaceConfig;
  const workspaceKey = getWorkspaceId() ?? 'default';
  const userKey = getMembershipId() ?? 'anon';

  const { connections, connectionId, setConnectionId, connection } = useCommerceConnections();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(cfg.defaultPageSize ?? 25);
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [sortCol, setSortCol] = useState(cfg.defaultSort ?? 'sku');
  const [order, setOrder] = useState<'asc' | 'desc'>(cfg.defaultOrder ?? 'asc');
  const [statusFilter, setStatusFilter] = useState('');
  const [result, setResult] = useState<CatalogProductPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [latencyMs, setLatencyMs] = useState<number | undefined>();
  const [bulkMsg, setBulkMsg] = useState('');

  useCommerceQueryBootstrap({
    onQuery: (uq) => {
      setQ(uq);
      setQDraft(uq);
    },
    statusParam: 'status',
    allowedStatuses: PRODUCT_STATUS_OPTS,
    onStatus: setStatusFilter,
  });

  const health = useCommerceDomainHealth(connectionId, 'catalog');
  const sortParam = order === 'desc' ? `${sortCol}_desc` : sortCol;

  const load = useCallback(async () => {
    if (!connectionId) {
      setLoading(false);
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const res = await amazonConnectApi.listCatalogProductsPage(connectionId, {
        page,
        page_size: pageSize,
        q: q || undefined,
        sort: sortParam,
        order,
        status: statusFilter || undefined,
      });
      setLatencyMs(Math.round(performance.now() - t0));
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load products');
      setResult(null);
      setLatencyMs(undefined);
    } finally {
      setLoading(false);
    }
  }, [connectionId, page, pageSize, q, sortParam, order, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const products: CatalogProduct[] = result?.products ?? [];

  const summary = useMemo(
    () =>
      summarizeProducts(products, {
        totalCount: result?.total_count,
        returnedCount: result?.returned_count ?? result?.count ?? products.length,
        amazonCount: health.amazonCount,
        hasFilter: Boolean(q || statusFilter),
      }),
    [products, result, health.amazonCount, q, statusFilter],
  );

  const meta = buildCommerceGridMeta(result, {
    latencyMs,
    lastSync: health.lastSync || connection?.last_sync_at,
    amazonCount: health.amazonCount,
    dataSourceBadge: cfg.dataSourceBadge,
    includeMismatchReason: true,
    entityLabel: 'products',
  });

  const columns: DataGridColumn<CatalogProduct>[] = useMemo(
    () => [
      {
        id: 'image',
        header: 'Image',
        group: 'Identity',
        width: 56,
        hideable: true,
        render: (p) => {
          const src = imageUrl(p);
          return src ? (
            <img src={src} alt="" className="h-8 w-8 object-contain" loading="lazy" width={32} height={32} />
          ) : (
            <ComingSoonCell />
          );
        },
        exportValue: (p) => imageUrl(p) ?? '',
      },
      {
        id: 'asin',
        header: 'ASIN',
        group: 'Identity',
        width: 120,
        sortable: true,
        stickyLeft: 0,
        render: (p) =>
          p.asin ? (
            <AsinLink asin={p.asin} marketplaceId={p.marketplace_id} connectionId={connectionId} />
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (p) => p.asin ?? '',
      },
      {
        id: 'parent_asin',
        header: 'Parent ASIN',
        group: 'Identity',
        width: 120,
        hideable: true,
        render: (p) =>
          p.parent_asin ? (
            <AsinLink asin={p.parent_asin} marketplaceId={p.marketplace_id} connectionId={connectionId} />
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (p) => p.parent_asin ?? '',
      },
      {
        id: 'sku',
        header: 'SKU',
        group: 'Identity',
        width: 120,
        sortable: true,
        render: (p) => (p.sku ? <SkuLink sku={p.sku} connectionId={connectionId} /> : <ComingSoonCell />),
        exportValue: (p) => p.sku,
      },
      {
        id: 'title',
        header: 'Title',
        group: 'Content',
        width: 220,
        sortable: true,
        render: (p) =>
          p.title ? (
            <span className="truncate" title={p.title}>
              {p.title}
            </span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (p) => p.title ?? '',
      },
      {
        id: 'brand',
        header: 'Brand',
        group: 'Content',
        width: 100,
        sortable: true,
        render: (p) => (p.brand ? p.brand : <ComingSoonCell />),
        exportValue: (p) => p.brand ?? '',
      },
      {
        id: 'category',
        header: 'Product type',
        group: 'Content',
        width: 120,
        render: (p) => {
          const t = p.product_type ?? p.category;
          return t ? t : <ComingSoonCell />;
        },
        exportValue: (p) => p.product_type ?? p.category ?? '',
      },
      {
        id: 'marketplace',
        header: 'Marketplace',
        group: 'Listing',
        width: 110,
        render: (p) =>
          p.marketplace_id ? (
            <span className="font-mono">{p.marketplace_id}</span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (p) => p.marketplace_id ?? '',
      },
      {
        id: 'status',
        header: 'Status',
        group: 'Listing',
        width: 120,
        render: (p) => {
          const st = productStatusText(p.status);
          return st ? (
            <span className="rounded-md bg-[var(--shell-hover)] px-1.5 py-0.5 text-[11px] font-medium">
              {st}
            </span>
          ) : (
            <ComingSoonCell />
          );
        },
        exportValue: (p) => productStatusText(p.status),
      },
      {
        id: 'fulfillment',
        header: 'Fulfilment',
        group: 'Listing',
        width: 100,
        render: (p) => (p.fulfillment ? p.fulfillment : <ComingSoonCell />),
        exportValue: (p) => p.fulfillment ?? '',
      },
      {
        id: 'price',
        header: 'Price (RO)',
        group: 'Peer (RO)',
        width: 88,
        render: (p) =>
          p.price ? (
            <Link
              to={`/amazon/pricing?connection=${encodeURIComponent(connectionId)}&q=${encodeURIComponent(p.sku || '')}`}
              className="font-mono text-[var(--shell-accent)] underline-offset-2 hover:underline"
              title="Owned by Pricing workspace — open Pricing"
            >
              {p.price}
            </Link>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (p) => p.price ?? '',
      },
      {
        id: 'inventory',
        header: 'Inv (RO)',
        group: 'Peer (RO)',
        width: 80,
        render: (p) => {
          const v =
            p.inventory_reference != null
              ? String(p.inventory_reference)
              : p.inventory != null
                ? String(p.inventory)
                : null;
          if (!v) return <ComingSoonCell />;
          return (
            <Link
              to={`/amazon/inventory?connection=${encodeURIComponent(connectionId)}&q=${encodeURIComponent(p.sku || '')}`}
              className="font-mono text-[var(--shell-accent)] underline-offset-2 hover:underline"
              title="Owned by Inventory — open Inventory"
            >
              {v}
            </Link>
          );
        },
        exportValue: (p) =>
          p.inventory_reference != null
            ? String(p.inventory_reference)
            : p.inventory != null
              ? String(p.inventory)
              : '',
      },
      {
        id: 'source_raw',
        header: 'Raw ID',
        group: 'Provenance',
        width: 100,
        hideable: true,
        render: (p) =>
          p.source_raw_id ? (
            <span className="font-mono text-[10px]" title="marketsync_raw_envelopes">
              {p.source_raw_id.slice(0, 8)}…
            </span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (p) => p.source_raw_id ?? '',
      },
      {
        id: 'updated',
        header: 'Updated',
        group: 'Provenance',
        width: 140,
        render: (p) =>
          p.updated_at ? (
            <span className="font-mono text-[10px]">{p.updated_at}</span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (p) => p.updated_at ?? '',
      },
    ],
    [connectionId],
  );

  const selectedRows = useMemo(() => {
    if (selected.size === 0) return [] as CatalogProduct[];
    return products.filter((p) => selected.has(`${p.sku}|${p.asin ?? ''}`));
  }, [products, selected]);

  const applyStatus = (value: string) => {
    setStatusFilter(value);
    setPage(1);
    setSelected(new Set());
  };

  const onConnectionChange = (id: string) => {
    setConnectionId(id);
    setPage(1);
    setSelected(new Set());
  };

  const statusToolbar = cfg.toolbarFilters?.[0];

  return (
    <FounderCommerceShell
      domain={cfg.domain}
      title={cfg.title}
      subtitle={cfg.subtitle}
      entryKey={cfg.entryKey}
      connection={connection}
      domainBadge={health.quality || health.status || 'catalog'}
      quickFilters={
        cfg.quickFilters ? (
          <QuickFilterBar
            filters={cfg.quickFilters}
            value={statusFilter}
            onChange={applyStatus}
            ariaLabel="Quick status filters"
          />
        ) : null
      }
      primaryActions={<SyncOnConnectAction />}
      summary={
        connectionId ? (
          <ProductSummaryStrip
            summary={summary}
            lastSync={health.lastSync || connection?.last_sync_at}
            source={result?.source || cfg.dataSourceBadge}
          />
        ) : null
      }
      legend={<WorkspaceLegend />}
    >
      {!connectionId && !loading ? <NoConnectionBanner /> : null}

      <PlatformDataGrid
        gridId={cfg.gridId}
        workspaceKey={workspaceKey}
        userKey={userKey}
        columns={columns}
        rows={products}
        rowKey={(p) => `${p.sku}|${p.asin ?? ''}`}
        meta={meta}
        loading={loading}
        error={error}
        emptyMessage={cfg.emptyMessage}
        emptyAction={<ConnectEmptyAction />}
        page={page}
        pageSize={pageSize}
        sort={sortCol}
        order={order}
        searchDraft={qDraft}
        searchApplied={q || undefined}
        onSearchDraftChange={setQDraft}
        onSearch={() => {
          setQ(qDraft.trim());
          setPage(1);
        }}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          if (PAGE_SIZE_OPTIONS.includes(n)) {
            setPageSize(n);
            setPage(1);
          }
        }}
        onSortChange={(col, ord) => {
          setSortCol(col);
          setOrder(ord);
          setPage(1);
        }}
        onRefresh={() => void load()}
        selectable
        selectedKeys={selected}
        onSelectionChange={setSelected}
        density={cfg.density ?? 'compact'}
        bulkActions={
          <BulkActionBar selectedCount={selected.size} message={bulkMsg}>
            <BulkActionButton
              onClick={() => {
                const asins = selectedRows.map((p) => p.asin).filter(Boolean) as string[];
                void copyLinesToClipboard(asins, 'No ASINs on selected rows').then((msg) =>
                  setBulkMsg(msg.includes('Copied') ? `${msg} ASIN(s)` : msg),
                );
              }}
            >
              Copy ASINs
            </BulkActionButton>
            <BulkActionButton
              onClick={() => {
                const skus = selectedRows.map((p) => p.sku).filter(Boolean);
                void copyLinesToClipboard(skus, 'No SKUs selected').then((msg) =>
                  setBulkMsg(msg.includes('Copied') ? `${msg} SKU(s)` : msg),
                );
              }}
            >
              Copy SKUs
            </BulkActionButton>
            <Link
              to={`/amazon/inventory?connection=${encodeURIComponent(connectionId)}`}
              className="inline-flex min-h-11 items-center rounded-[var(--shell-radius-md)] border border-[var(--shell-border)] bg-[var(--shell-surface-elevated)] px-3 text-sm"
            >
              Open Inventory
            </Link>
          </BulkActionBar>
        }
        toolbarExtra={
          <>
            <ConnectionToolbarSelect
              connections={connections}
              value={connectionId}
              onChange={onConnectionChange}
            />
            {statusToolbar ? (
              <ToolbarFilterSelect
                label={statusToolbar.label}
                value={statusFilter}
                options={statusToolbar.options}
                onChange={applyStatus}
              />
            ) : null}
          </>
        }
      />
    </FounderCommerceShell>
  );
}
